// import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { NavLink } from 'react-router-dom';

import logoSrc from '../../assets/logo-dark.svg';
import * as NavIcons from './sidebarIcons';
import { breakpoints } from '../../breakpoints';
import { Button } from '../UI/Button';

const SidebarInfo = styled.header`
  padding: 50px 64px;

  background-color: ${props => props.theme.bg1};

  transition: all 0.05s ease-out;

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: flex;
  }
`;

const Logo = styled.img`
  display: none;

  width: 33.4px;
  height: 26.7px;
  margin: 0 auto;

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const TextLogo = styled.h1`
  margin: 0 0 30px;

  font-family: Bebas Neue, sans-serif;
  font-size: 32px;
  font-weight: 600;
  text-transform: uppercase;

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const SidebarNavWrapper = styled.div`
  background-color: ${props => props.theme.bg1};

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0;

  padding: 48px;

  transition: all 0.05s ease-out;
`;

const SidebarNavItem = styled.button<{ active: boolean }>`
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: center;
  gap: 12px;

  width: 100%;
  padding: 8px 16px;

  border: none;
  border-radius: 8px;
  background: none;

  text-align: left;
  font-size: 14px;
  font-weight: 600;
  font-family: Poppins, sans-serif;
  color: ${props => props.theme.text2};
  white-space: nowrap;
  text-decoration: none;

  cursor: pointer;

  svg {
    width: 32px;
    height: 32px;
  }

  &:hover {
    color: #2d314e;

    svg path {
      fill: #2d314e;
    }
  }

  ${props =>
    props.active &&
    css`
      background-color: #ececf7;

      color: #2d314e;

      svg path {
        fill: #2d314e;
      }
    `}
`;

const SidebarNavItemLabel = styled.span`
  text-decoration: none;
`;

const SidebarInfoItem = styled.div`
  white-space: nowrap;

  & + & {
    margin-top: 18px;
  }
`;

const SidebarInfoLabel = styled.span`
  font-size: 12px;
  color: ${props => props.theme.text2};
`;

const SidebarInfoValue = styled.p`
  margin: 10px 0 0;

  font-size: 20px;
  font-family: Bebas Neue, sans-serif;
  font-weight: bold;
  color: ${props => props.theme.text0};
`;

const SidebarInfoExtra = styled.small<{ type: ChangeType }>`
  margin-left: 8px;

  font-size: 12px;
  font-family: Poppins, sans-serif;
  font-weight: 400;
  color: ${props => {
    switch (props.type) {
      case 'INCREASE':
        return props.theme.accent0;
      case 'DECREASE':
        return props.theme.text0;
      case 'STILL':
      default:
        return '#6a6f83';
    }
  }};
`;

const SidebarStatus = styled.footer`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 32px 40px 32px 64px;

  background-color: ${props => props.theme.bg1};

  transition: all 0.05s ease-out;

  @media screen and (max-width: ${breakpoints.tablet}) {
    background-color: ${props => props.theme.sidebar0};
  }
`;

const WalletIcon = styled.div`
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;

    position: absolute;
    left: -2px;
    bottom: 6px;

    border-radius: 50%;
    border: 3px solid #16161c;
    background-color: #7cf9ba;
  }
`;

const StatusWalletInfo = styled.div``;

const LogoutButton = styled(Button)`
  margin-left: auto;
`;

const HideButton = styled.button`
  width: 100%;
  height: 44px;

  background-color: #fff;
  border-radius: 6px;
  border: none;
  color: ${props => props.theme.text2};

  cursor: pointer;
`;

const narrowSidebarStyles = css`
  width: 112px;

  ${TextLogo}, ${SidebarInfoItem},
  ${LogoutButton}, ${StatusWalletInfo}, ${SidebarNavItemLabel} {
    display: none;
  }

  ${Logo} {
    display: block;
  }

  ${SidebarInfo} {
    padding: 60px 38px 45px;
  }

  ${SidebarNav} {
    padding: 32px 40px;
  }

  ${SidebarNavItem} {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 56px;
    height: 56px;
    padding: 0;
  }

  ${SidebarStatus} {
    gap: 40px;

    padding: 40px 34px;
  }

  ${WalletIcon} {
    margin: 0 auto;
  }

  ${HideButton} {
    width: 44px;
  }
`;

const MenuButton = styled.button`
  display: none;

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: block;
  }
`;

const SidebarWrapper = styled.aside<{ tight?: boolean }>`
  display: grid;
  grid-template-rows: auto 1fr auto;

  width: 280px;
  height: 100%;

  border-right: 1px solid ${props => props.theme.border};

  color: #000;

  transition: width 0.05s ease-out;
  overflow: hidden;
  z-index: 2;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    ${props => props.tight && narrowSidebarStyles};
  }

  @media screen and (max-width: ${breakpoints.tablet}) {
    grid-template-rows: none;
    grid-template-columns: 32px 1fr auto;
    gap: 45px;

    width: 100%;
    height: 128px;
  }
`;

const navItems: NavItem[] = [
  { key: 'wallet', label: 'Wallet', icon: NavIcons.IconWallet },
  { key: 'bridge', label: 'Bridge', icon: NavIcons.IconBridge },
  // { key: 'exchange', label: 'Exchange', icon: NavIcons.IconExchange },
  // { key: 'bonding', label: 'Bonding Curve', icon: NavIcons.IconBonding },
  // { key: 'staking', label: 'Staking', icon: NavIcons.IconDefault },
  // { key: 'nfts', label: 'NFTs', icon: NavIcons.IconDefault },
  // { key: 'account', label: 'My Account', icon: NavIcons.IconDefault },
  // { key: "BRIDGE", label: "Bridge", icon: NavIcons.IconDefault },
];

export function Sidebar() {
  // const [tight, setTight] = useState(true);
  // const resizeable = useMemo(() => activeService === 'EXCHANGE', [activeService]);

  const info: InfoItem[] = [
    {
      name: 'NOM-USDT',
      value: '$10.12',
      extra: '24.2%',
      extraType: 'INCREASE',
    },
    {
      name: 'NOM-ETH',
      value: '0.0731',
      extra: '18.7%',
      extraType: 'DECREASE',
    },
    // {
    //   name: 'NOM ISSUED',
    //   value: '14,024,810',
    //   extra: '/ 54,112,321',
    //   extraType: 'STILL',
    // },
  ];

  return (
    <SidebarWrapper tight={false}>
      <MenuButton>x</MenuButton>
      <SidebarInfo>
        <Logo src={logoSrc} alt="" />
        <TextLogo>Onomy</TextLogo>

        {info.map((infoItem: InfoItem) => (
          <SidebarInfoItem key={infoItem.name}>
            <SidebarInfoLabel>{infoItem.name}</SidebarInfoLabel>
            <SidebarInfoValue>
              {infoItem.value}{' '}
              <SidebarInfoExtra type={infoItem.extraType}>{infoItem.extra}</SidebarInfoExtra>
            </SidebarInfoValue>
          </SidebarInfoItem>
        ))}
      </SidebarInfo>
      <SidebarNavWrapper>
        <SidebarNav>
          {navItems.map(item => (
            <NavLink
              key={item.key}
              to={`/${item.key}`}
              role="tab"
              title={item.label}
              style={{ textDecoration: 'none' }}
            >
              {({ isActive }) => (
                <SidebarNavItem active={isActive}>
                  <item.icon /> <span>{item.label}</span>
                </SidebarNavItem>
              )}
            </NavLink>
          ))}
        </SidebarNav>
      </SidebarNavWrapper>
      <SidebarStatus>
        <LogoutButton>Logout / Switch</LogoutButton>

        {/* {resizeable && (
          <HideButton onClick={() => setTight(!tight)}>{tight ? `>` : `< Hide menu`}</HideButton>
        )} */}
      </SidebarStatus>
    </SidebarWrapper>
  );
}
